import { warn, inject, ref, computed, unref, provide, shallowRef, watchEffect, defineComponent as defineComponent$1, createVNode, mergeProps, toRefs, isRef, toRef, Text, h, TransitionGroup, Transition, watch, onBeforeMount, nextTick, onBeforeUnmount, withDirectives, Fragment, resolveDirective, vShow, onMounted, triggerRef } from "vue";
import { u as useDefaults } from "./_plugin-vue_export-helper-3194dc60.mjs";
import { t as toSubjectListString, s as stringOr, D as DateTime, f as fromMarkdownLike } from "./helpers-674591c1.mjs";
import { g as getDisplayNameOfParticipant } from "./malarkeyTwilioAdapter-1c296255.mjs";
import { i as makeVListItemProps } from "./_VListItem-508e4b13.mjs";
import "./index-64135f7d.mjs";
import "./i18n-35335a5c.mjs";
import "./_i18n-e414d464.mjs";
import "./event-socket/malarkeyEventSocketAdapter.es.js";
import { d as debug } from "./_debug-8b5c3032.mjs";
import "./otpauth.esm-675c01d0.mjs";
import "./sudo-bad5b55e.mjs";
import { M as MalarkeyContactAvatar } from "./MalarkeyContactAvatar-1b079ba3.mjs";
import { p as propsFactory, m as makeThemeProps, a as provideTheme } from "./theme-96176369.mjs";
import { h as has, c as chunk, p as padEnd, a as getCurrentInstance, m as mergeDeep, t as toKebabCase, b as pick, d as destructComputed, g as getCurrentInstanceName, i as includes, e as convertToUnit, f as flattenFragments } from "./getCurrentInstance-eec76705.mjs";
const IN_BROWSER = typeof window !== "undefined";
const SUPPORTS_INTERSECTION = IN_BROWSER && "IntersectionObserver" in window;
const mainTRC = 2.4;
const Rco = 0.2126729;
const Gco = 0.7151522;
const Bco = 0.072175;
const normBG = 0.55;
const normTXT = 0.58;
const revTXT = 0.57;
const revBG = 0.62;
const blkThrs = 0.03;
const blkClmp = 1.45;
const deltaYmin = 5e-4;
const scaleBoW = 1.25;
const scaleWoB = 1.25;
const loConThresh = 0.078;
const loConFactor = 12.82051282051282;
const loConOffset = 0.06;
const loClip = 1e-3;
function APCAcontrast(text, background) {
  const Rtxt = (text.r / 255) ** mainTRC;
  const Gtxt = (text.g / 255) ** mainTRC;
  const Btxt = (text.b / 255) ** mainTRC;
  const Rbg = (background.r / 255) ** mainTRC;
  const Gbg = (background.g / 255) ** mainTRC;
  const Bbg = (background.b / 255) ** mainTRC;
  let Ytxt = Rtxt * Rco + Gtxt * Gco + Btxt * Bco;
  let Ybg = Rbg * Rco + Gbg * Gco + Bbg * Bco;
  if (Ytxt <= blkThrs)
    Ytxt += (blkThrs - Ytxt) ** blkClmp;
  if (Ybg <= blkThrs)
    Ybg += (blkThrs - Ybg) ** blkClmp;
  if (Math.abs(Ybg - Ytxt) < deltaYmin)
    return 0;
  let outputContrast;
  if (Ybg > Ytxt) {
    const SAPC = (Ybg ** normBG - Ytxt ** normTXT) * scaleBoW;
    outputContrast = SAPC < loClip ? 0 : SAPC < loConThresh ? SAPC - SAPC * loConFactor * loConOffset : SAPC - loConOffset;
  } else {
    const SAPC = (Ybg ** revBG - Ytxt ** revTXT) * scaleWoB;
    outputContrast = SAPC > -loClip ? 0 : SAPC > -loConThresh ? SAPC - SAPC * loConFactor * loConOffset : SAPC + loConOffset;
  }
  return outputContrast * 100;
}
function consoleWarn(message) {
  warn(`Vuetify: ${message}`);
}
function isCssColor(color) {
  return !!color && /^(#|var\(--|(rgb|hsl)a?\()/.test(color);
}
function isParsableColor(color) {
  return isCssColor(color) && !/^((rgb|hsl)a?\()?var\(--/.test(color);
}
const cssColorRe = /^(?<fn>(?:rgb|hsl)a?)\((?<values>.+)\)/;
const mappers = {
  rgb: (r, g, b, a) => ({
    r,
    g,
    b,
    a
  }),
  rgba: (r, g, b, a) => ({
    r,
    g,
    b,
    a
  }),
  hsl: (h2, s, l, a) => HSLtoRGB({
    h: h2,
    s,
    l,
    a
  }),
  hsla: (h2, s, l, a) => HSLtoRGB({
    h: h2,
    s,
    l,
    a
  }),
  hsv: (h2, s, v, a) => HSVtoRGB({
    h: h2,
    s,
    v,
    a
  }),
  hsva: (h2, s, v, a) => HSVtoRGB({
    h: h2,
    s,
    v,
    a
  })
};
function parseColor(color) {
  if (typeof color === "number") {
    if (isNaN(color) || color < 0 || color > 16777215) {
      consoleWarn(`'${color}' is not a valid hex color`);
    }
    return {
      r: (color & 16711680) >> 16,
      g: (color & 65280) >> 8,
      b: color & 255
    };
  } else if (typeof color === "string" && cssColorRe.test(color)) {
    const {
      groups
    } = color.match(cssColorRe);
    const {
      fn,
      values
    } = groups;
    const realValues = values.split(/,\s*/).map((v) => {
      if (v.endsWith("%") && ["hsl", "hsla", "hsv", "hsva"].includes(fn)) {
        return parseFloat(v) / 100;
      } else {
        return parseFloat(v);
      }
    });
    return mappers[fn](...realValues);
  } else if (typeof color === "string") {
    let hex = color.startsWith("#") ? color.slice(1) : color;
    if ([3, 4].includes(hex.length)) {
      hex = hex.split("").map((char) => char + char).join("");
    } else if (![6, 8].includes(hex.length)) {
      consoleWarn(`'${color}' is not a valid hex(a) color`);
    }
    const int = parseInt(hex, 16);
    if (isNaN(int) || int < 0 || int > 4294967295) {
      consoleWarn(`'${color}' is not a valid hex(a) color`);
    }
    return HexToRGB(hex);
  } else if (typeof color === "object") {
    if (has(color, ["r", "g", "b"])) {
      return color;
    } else if (has(color, ["h", "s", "l"])) {
      return HSVtoRGB(HSLtoHSV(color));
    } else if (has(color, ["h", "s", "v"])) {
      return HSVtoRGB(color);
    }
  }
  throw new TypeError(`Invalid color: ${color == null ? color : String(color) || color.constructor.name}
Expected #hex, #hexa, rgb(), rgba(), hsl(), hsla(), object or number`);
}
function HSVtoRGB(hsva) {
  const {
    h: h2,
    s,
    v,
    a
  } = hsva;
  const f = (n) => {
    const k = (n + h2 / 60) % 6;
    return v - v * s * Math.max(Math.min(k, 4 - k, 1), 0);
  };
  const rgb = [f(5), f(3), f(1)].map((v2) => Math.round(v2 * 255));
  return {
    r: rgb[0],
    g: rgb[1],
    b: rgb[2],
    a
  };
}
function HSLtoRGB(hsla) {
  return HSVtoRGB(HSLtoHSV(hsla));
}
function HSLtoHSV(hsl) {
  const {
    h: h2,
    s,
    l,
    a
  } = hsl;
  const v = l + s * Math.min(l, 1 - l);
  const sprime = v === 0 ? 0 : 2 - 2 * l / v;
  return {
    h: h2,
    s: sprime,
    v,
    a
  };
}
function HexToRGB(hex) {
  hex = parseHex(hex);
  let [r, g, b, a] = chunk(hex, 2).map((c) => parseInt(c, 16));
  a = a === void 0 ? a : a / 255;
  return {
    r,
    g,
    b,
    a
  };
}
function parseHex(hex) {
  if (hex.startsWith("#")) {
    hex = hex.slice(1);
  }
  hex = hex.replace(/([^0-9a-f])/gi, "F");
  if (hex.length === 3 || hex.length === 4) {
    hex = hex.split("").map((x) => x + x).join("");
  }
  if (hex.length !== 6) {
    hex = padEnd(padEnd(hex, 6), 8, "F");
  }
  return hex;
}
function getForeground(color) {
  const blackContrast = Math.abs(APCAcontrast(parseColor(0), parseColor(color)));
  const whiteContrast = Math.abs(APCAcontrast(parseColor(16777215), parseColor(color)));
  return whiteContrast > Math.min(blackContrast, 50) ? "#fff" : "#000";
}
const makeComponentProps = propsFactory({
  class: [String, Array, Object],
  style: {
    type: [String, Array, Object],
    default: null
  }
}, "component");
function injectSelf(key) {
  let vm = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : getCurrentInstance("injectSelf");
  const {
    provides
  } = vm;
  if (provides && key in provides) {
    return provides[key];
  }
  return void 0;
}
const DefaultsSymbol = Symbol.for("vuetify:defaults");
function injectDefaults() {
  const defaults = inject(DefaultsSymbol);
  if (!defaults)
    throw new Error("[Vuetify] Could not find defaults instance");
  return defaults;
}
function provideDefaults(defaults, options) {
  const injectedDefaults = injectDefaults();
  const providedDefaults = ref(defaults);
  const newDefaults = computed(() => {
    const disabled = unref(options == null ? void 0 : options.disabled);
    if (disabled)
      return injectedDefaults.value;
    const scoped = unref(options == null ? void 0 : options.scoped);
    const reset = unref(options == null ? void 0 : options.reset);
    const root = unref(options == null ? void 0 : options.root);
    if (providedDefaults.value == null && !(scoped || reset || root))
      return injectedDefaults.value;
    let properties = mergeDeep(providedDefaults.value, {
      prev: injectedDefaults.value
    });
    if (scoped)
      return properties;
    if (reset || root) {
      const len = Number(reset || Infinity);
      for (let i = 0; i <= len; i++) {
        if (!properties || !("prev" in properties)) {
          break;
        }
        properties = properties.prev;
      }
      if (properties && typeof root === "string" && root in properties) {
        properties = mergeDeep(mergeDeep(properties, {
          prev: properties
        }), properties[root]);
      }
      return properties;
    }
    return properties.prev ? mergeDeep(properties.prev, properties) : properties;
  });
  provide(DefaultsSymbol, newDefaults);
  return newDefaults;
}
function propIsDefined(vnode, prop) {
  var _a, _b;
  return typeof ((_a = vnode.props) == null ? void 0 : _a[prop]) !== "undefined" || typeof ((_b = vnode.props) == null ? void 0 : _b[toKebabCase(prop)]) !== "undefined";
}
function internalUseDefaults() {
  let props = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
  let name = arguments.length > 1 ? arguments[1] : void 0;
  let defaults = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : injectDefaults();
  const vm = getCurrentInstance("useDefaults");
  name = name ?? vm.type.name ?? vm.type.__name;
  if (!name) {
    throw new Error("[Vuetify] Could not determine component name");
  }
  const componentDefaults = computed(() => {
    var _a;
    return (_a = defaults.value) == null ? void 0 : _a[props._as ?? name];
  });
  const _props = new Proxy(props, {
    get(target, prop) {
      var _a, _b, _c, _d, _e, _f, _g;
      const propValue = Reflect.get(target, prop);
      if (prop === "class" || prop === "style") {
        return [(_a = componentDefaults.value) == null ? void 0 : _a[prop], propValue].filter((v) => v != null);
      } else if (typeof prop === "string" && !propIsDefined(vm.vnode, prop)) {
        return ((_b = componentDefaults.value) == null ? void 0 : _b[prop]) !== void 0 ? (_c = componentDefaults.value) == null ? void 0 : _c[prop] : ((_e = (_d = defaults.value) == null ? void 0 : _d.global) == null ? void 0 : _e[prop]) !== void 0 ? (_g = (_f = defaults.value) == null ? void 0 : _f.global) == null ? void 0 : _g[prop] : propValue;
      }
      return propValue;
    }
  });
  const _subcomponentDefaults = shallowRef();
  watchEffect(() => {
    if (componentDefaults.value) {
      const subComponents = Object.entries(componentDefaults.value).filter((_ref) => {
        let [key] = _ref;
        return key.startsWith(key[0].toUpperCase());
      });
      _subcomponentDefaults.value = subComponents.length ? Object.fromEntries(subComponents) : void 0;
    } else {
      _subcomponentDefaults.value = void 0;
    }
  });
  function provideSubDefaults() {
    const injected = injectSelf(DefaultsSymbol, vm);
    provide(DefaultsSymbol, computed(() => {
      return _subcomponentDefaults.value ? mergeDeep((injected == null ? void 0 : injected.value) ?? {}, _subcomponentDefaults.value) : injected == null ? void 0 : injected.value;
    }));
  }
  return {
    props: _props,
    provideSubDefaults
  };
}
function defineComponent(options) {
  options._setup = options._setup ?? options.setup;
  if (!options.name) {
    consoleWarn("The component is missing an explicit name, unable to generate default prop value");
    return options;
  }
  if (options._setup) {
    options.props = propsFactory(options.props ?? {}, options.name)();
    const propKeys = Object.keys(options.props).filter((key) => key !== "class" && key !== "style");
    options.filterProps = function filterProps(props) {
      return pick(props, propKeys);
    };
    options.props._as = String;
    options.setup = function setup(props, ctx) {
      const defaults = injectDefaults();
      if (!defaults.value)
        return options._setup(props, ctx);
      const {
        props: _props,
        provideSubDefaults
      } = internalUseDefaults(props, props._as ?? options.name, defaults);
      const setupBindings = options._setup(_props, ctx);
      provideSubDefaults();
      return setupBindings;
    };
  }
  return options;
}
function genericComponent() {
  let exposeDefaults = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : true;
  return (options) => (exposeDefaults ? defineComponent : defineComponent$1)(options);
}
function useRender(render) {
  const vm = getCurrentInstance("useRender");
  vm.render = render;
}
const IconValue = [String, Function, Object, Array];
const IconSymbol = Symbol.for("vuetify:icons");
const makeIconProps = propsFactory({
  icon: {
    type: IconValue
  },
  // Could not remove this and use makeTagProps, types complained because it is not required
  tag: {
    type: String,
    required: true
  }
}, "icon");
const VComponentIcon = genericComponent()({
  name: "VComponentIcon",
  props: makeIconProps(),
  setup(props, _ref) {
    let {
      slots
    } = _ref;
    return () => {
      const Icon = props.icon;
      return createVNode(props.tag, null, {
        default: () => {
          var _a;
          return [props.icon ? createVNode(Icon, null, null) : (_a = slots.default) == null ? void 0 : _a.call(slots)];
        }
      });
    };
  }
});
const VSvgIcon = defineComponent({
  name: "VSvgIcon",
  inheritAttrs: false,
  props: makeIconProps(),
  setup(props, _ref2) {
    let {
      attrs
    } = _ref2;
    return () => {
      return createVNode(props.tag, mergeProps(attrs, {
        "style": null
      }), {
        default: () => [createVNode("svg", {
          "class": "v-icon__svg",
          "xmlns": "http://www.w3.org/2000/svg",
          "viewBox": "0 0 24 24",
          "role": "img",
          "aria-hidden": "true"
        }, [Array.isArray(props.icon) ? props.icon.map((path) => Array.isArray(path) ? createVNode("path", {
          "d": path[0],
          "fill-opacity": path[1]
        }, null) : createVNode("path", {
          "d": path
        }, null)) : createVNode("path", {
          "d": props.icon
        }, null)])]
      });
    };
  }
});
defineComponent({
  name: "VLigatureIcon",
  props: makeIconProps(),
  setup(props) {
    return () => {
      return createVNode(props.tag, null, {
        default: () => [props.icon]
      });
    };
  }
});
defineComponent({
  name: "VClassIcon",
  props: makeIconProps(),
  setup(props) {
    return () => {
      return createVNode(props.tag, {
        "class": props.icon
      }, null);
    };
  }
});
const useIcon = (props) => {
  const icons = inject(IconSymbol);
  if (!icons)
    throw new Error("Missing Vuetify Icons provide!");
  const iconData = computed(() => {
    var _a;
    const iconAlias = unref(props);
    if (!iconAlias)
      return {
        component: VComponentIcon
      };
    let icon = iconAlias;
    if (typeof icon === "string") {
      icon = icon.trim();
      if (icon.startsWith("$")) {
        icon = (_a = icons.aliases) == null ? void 0 : _a[icon.slice(1)];
      }
    }
    if (!icon)
      consoleWarn(`Could not find aliased icon "${iconAlias}"`);
    if (Array.isArray(icon)) {
      return {
        component: VSvgIcon,
        icon
      };
    } else if (typeof icon !== "string") {
      return {
        component: VComponentIcon,
        icon
      };
    }
    const iconSetName = Object.keys(icons.sets).find((setName) => typeof icon === "string" && icon.startsWith(`${setName}:`));
    const iconName = iconSetName ? icon.slice(iconSetName.length + 1) : icon;
    const iconSet = icons.sets[iconSetName ?? icons.defaultSet];
    return {
      component: iconSet.component,
      icon: iconName
    };
  });
  return {
    iconData
  };
};
const VAvatar$1 = "";
const makeVDefaultsProviderProps = propsFactory({
  defaults: Object,
  disabled: Boolean,
  reset: [Number, String],
  root: [Boolean, String],
  scoped: Boolean
}, "VDefaultsProvider");
const VDefaultsProvider = genericComponent(false)({
  name: "VDefaultsProvider",
  props: makeVDefaultsProviderProps(),
  setup(props, _ref) {
    let {
      slots
    } = _ref;
    const {
      defaults,
      disabled,
      reset,
      root,
      scoped
    } = toRefs(props);
    provideDefaults(defaults, {
      reset,
      root,
      scoped,
      disabled
    });
    return () => {
      var _a;
      return (_a = slots.default) == null ? void 0 : _a.call(slots);
    };
  }
});
const VIcon$1 = "";
function useColor(colors) {
  return destructComputed(() => {
    const classes = [];
    const styles = {};
    if (colors.value.background) {
      if (isCssColor(colors.value.background)) {
        styles.backgroundColor = colors.value.background;
        if (!colors.value.text && isParsableColor(colors.value.background)) {
          const backgroundColor = parseColor(colors.value.background);
          if (backgroundColor.a == null || backgroundColor.a === 1) {
            const textColor = getForeground(backgroundColor);
            styles.color = textColor;
            styles.caretColor = textColor;
          }
        }
      } else {
        classes.push(`bg-${colors.value.background}`);
      }
    }
    if (colors.value.text) {
      if (isCssColor(colors.value.text)) {
        styles.color = colors.value.text;
        styles.caretColor = colors.value.text;
      } else {
        classes.push(`text-${colors.value.text}`);
      }
    }
    return {
      colorClasses: classes,
      colorStyles: styles
    };
  });
}
function useTextColor(props, name) {
  const colors = computed(() => ({
    text: isRef(props) ? props.value : name ? props[name] : null
  }));
  const {
    colorClasses: textColorClasses,
    colorStyles: textColorStyles
  } = useColor(colors);
  return {
    textColorClasses,
    textColorStyles
  };
}
function useBackgroundColor(props, name) {
  const colors = computed(() => ({
    background: isRef(props) ? props.value : name ? props[name] : null
  }));
  const {
    colorClasses: backgroundColorClasses,
    colorStyles: backgroundColorStyles
  } = useColor(colors);
  return {
    backgroundColorClasses,
    backgroundColorStyles
  };
}
const predefinedSizes = ["x-small", "small", "default", "large", "x-large"];
const makeSizeProps = propsFactory({
  size: {
    type: [String, Number],
    default: "default"
  }
}, "size");
function useSize(props) {
  let name = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : getCurrentInstanceName();
  return destructComputed(() => {
    let sizeClasses;
    let sizeStyles;
    if (includes(predefinedSizes, props.size)) {
      sizeClasses = `${name}--size-${props.size}`;
    } else if (props.size) {
      sizeStyles = {
        width: convertToUnit(props.size),
        height: convertToUnit(props.size)
      };
    }
    return {
      sizeClasses,
      sizeStyles
    };
  });
}
const makeTagProps = propsFactory({
  tag: {
    type: String,
    default: "div"
  }
}, "tag");
const makeVIconProps = propsFactory({
  color: String,
  disabled: Boolean,
  start: Boolean,
  end: Boolean,
  icon: IconValue,
  ...makeComponentProps(),
  ...makeSizeProps(),
  ...makeTagProps({
    tag: "i"
  }),
  ...makeThemeProps()
}, "VIcon");
const VIcon = genericComponent()({
  name: "VIcon",
  props: makeVIconProps(),
  setup(props, _ref) {
    let {
      attrs,
      slots
    } = _ref;
    const slotIcon = ref();
    const {
      themeClasses
    } = provideTheme(props);
    const {
      iconData
    } = useIcon(computed(() => slotIcon.value || props.icon));
    const {
      sizeClasses
    } = useSize(props);
    const {
      textColorClasses,
      textColorStyles
    } = useTextColor(toRef(props, "color"));
    useRender(() => {
      var _a, _b;
      const slotValue = (_a = slots.default) == null ? void 0 : _a.call(slots);
      if (slotValue) {
        slotIcon.value = (_b = flattenFragments(slotValue).filter((node) => node.type === Text && node.children && typeof node.children === "string")[0]) == null ? void 0 : _b.children;
      }
      const hasClick = !!(attrs.onClick || attrs.onClickOnce);
      return createVNode(iconData.value.component, {
        "tag": props.tag,
        "icon": iconData.value.icon,
        "class": ["v-icon", "notranslate", themeClasses.value, sizeClasses.value, textColorClasses.value, {
          "v-icon--clickable": hasClick,
          "v-icon--disabled": props.disabled,
          "v-icon--start": props.start,
          "v-icon--end": props.end
        }, props.class],
        "style": [!sizeClasses.value ? {
          fontSize: convertToUnit(props.size),
          height: convertToUnit(props.size),
          width: convertToUnit(props.size)
        } : void 0, textColorStyles.value, props.style],
        "role": hasClick ? "button" : void 0,
        "aria-hidden": !hasClick,
        "tabindex": hasClick ? props.disabled ? -1 : 0 : void 0
      }, {
        default: () => [slotValue]
      });
    });
    return {};
  }
});
const VImg$1 = "";
const VResponsive$1 = "";
const makeDimensionProps = propsFactory({
  height: [Number, String],
  maxHeight: [Number, String],
  maxWidth: [Number, String],
  minHeight: [Number, String],
  minWidth: [Number, String],
  width: [Number, String]
}, "dimension");
function useDimension(props) {
  const dimensionStyles = computed(() => {
    const styles = {};
    const height = convertToUnit(props.height);
    const maxHeight = convertToUnit(props.maxHeight);
    const maxWidth = convertToUnit(props.maxWidth);
    const minHeight = convertToUnit(props.minHeight);
    const minWidth = convertToUnit(props.minWidth);
    const width = convertToUnit(props.width);
    if (height != null)
      styles.height = height;
    if (maxHeight != null)
      styles.maxHeight = maxHeight;
    if (maxWidth != null)
      styles.maxWidth = maxWidth;
    if (minHeight != null)
      styles.minHeight = minHeight;
    if (minWidth != null)
      styles.minWidth = minWidth;
    if (width != null)
      styles.width = width;
    return styles;
  });
  return {
    dimensionStyles
  };
}
function useAspectStyles(props) {
  return {
    aspectStyles: computed(() => {
      const ratio = Number(props.aspectRatio);
      return ratio ? {
        paddingBottom: String(1 / ratio * 100) + "%"
      } : void 0;
    })
  };
}
const makeVResponsiveProps = propsFactory({
  aspectRatio: [String, Number],
  contentClass: null,
  inline: Boolean,
  ...makeComponentProps(),
  ...makeDimensionProps()
}, "VResponsive");
const VResponsive = genericComponent()({
  name: "VResponsive",
  props: makeVResponsiveProps(),
  setup(props, _ref) {
    let {
      slots
    } = _ref;
    const {
      aspectStyles
    } = useAspectStyles(props);
    const {
      dimensionStyles
    } = useDimension(props);
    useRender(() => {
      var _a;
      return createVNode("div", {
        "class": ["v-responsive", {
          "v-responsive--inline": props.inline
        }, props.class],
        "style": [dimensionStyles.value, props.style]
      }, [createVNode("div", {
        "class": "v-responsive__sizer",
        "style": aspectStyles.value
      }, null), (_a = slots.additional) == null ? void 0 : _a.call(slots), slots.default && createVNode("div", {
        "class": ["v-responsive__content", props.contentClass]
      }, [slots.default()])]);
    });
    return {};
  }
});
const makeRoundedProps = propsFactory({
  rounded: {
    type: [Boolean, Number, String],
    default: void 0
  },
  tile: Boolean
}, "rounded");
function useRounded(props) {
  let name = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : getCurrentInstanceName();
  const roundedClasses = computed(() => {
    const rounded = isRef(props) ? props.value : props.rounded;
    const tile = isRef(props) ? props.value : props.tile;
    const classes = [];
    if (rounded === true || rounded === "") {
      classes.push(`${name}--rounded`);
    } else if (typeof rounded === "string" || rounded === 0) {
      for (const value of String(rounded).split(" ")) {
        classes.push(`rounded-${value}`);
      }
    } else if (tile || rounded === false) {
      classes.push("rounded-0");
    }
    return classes;
  });
  return {
    roundedClasses
  };
}
const makeTransitionProps = propsFactory({
  transition: {
    type: [Boolean, String, Object],
    default: "fade-transition",
    validator: (val) => val !== true
  }
}, "transition");
const MaybeTransition = (props, _ref) => {
  let {
    slots
  } = _ref;
  const {
    transition,
    disabled,
    group,
    ...rest
  } = props;
  const {
    component = group ? TransitionGroup : Transition,
    ...customProps
  } = typeof transition === "object" ? transition : {};
  return h(component, mergeProps(typeof transition === "string" ? {
    name: disabled ? "" : transition
  } : customProps, typeof transition === "string" ? {} : Object.fromEntries(Object.entries({
    disabled,
    group
  }).filter((_ref2) => {
    let [_, v] = _ref2;
    return v !== void 0;
  })), rest), slots);
};
function mounted(el, binding) {
  if (!SUPPORTS_INTERSECTION)
    return;
  const modifiers = binding.modifiers || {};
  const value = binding.value;
  const {
    handler,
    options
  } = typeof value === "object" ? value : {
    handler: value,
    options: {}
  };
  const observer = new IntersectionObserver(function() {
    var _a;
    let entries = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : [];
    let observer2 = arguments.length > 1 ? arguments[1] : void 0;
    const _observe = (_a = el._observe) == null ? void 0 : _a[binding.instance.$.uid];
    if (!_observe)
      return;
    const isIntersecting = entries.some((entry) => entry.isIntersecting);
    if (handler && (!modifiers.quiet || _observe.init) && (!modifiers.once || isIntersecting || _observe.init)) {
      handler(isIntersecting, entries, observer2);
    }
    if (isIntersecting && modifiers.once)
      unmounted(el, binding);
    else
      _observe.init = true;
  }, options);
  el._observe = Object(el._observe);
  el._observe[binding.instance.$.uid] = {
    init: false,
    observer
  };
  observer.observe(el);
}
function unmounted(el, binding) {
  var _a;
  const observe = (_a = el._observe) == null ? void 0 : _a[binding.instance.$.uid];
  if (!observe)
    return;
  observe.observer.unobserve(el);
  delete el._observe[binding.instance.$.uid];
}
const Intersect = {
  mounted,
  unmounted
};
const intersect = Intersect;
const makeVImgProps = propsFactory({
  alt: String,
  cover: Boolean,
  color: String,
  draggable: {
    type: [Boolean, String],
    default: void 0
  },
  eager: Boolean,
  gradient: String,
  lazySrc: String,
  options: {
    type: Object,
    // For more information on types, navigate to:
    // https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
    default: () => ({
      root: void 0,
      rootMargin: void 0,
      threshold: void 0
    })
  },
  sizes: String,
  src: {
    type: [String, Object],
    default: ""
  },
  crossorigin: String,
  referrerpolicy: String,
  srcset: String,
  position: String,
  ...makeVResponsiveProps(),
  ...makeComponentProps(),
  ...makeRoundedProps(),
  ...makeTransitionProps()
}, "VImg");
const VImg = genericComponent()({
  name: "VImg",
  directives: {
    intersect
  },
  props: makeVImgProps(),
  emits: {
    loadstart: (value) => true,
    load: (value) => true,
    error: (value) => true
  },
  setup(props, _ref) {
    let {
      emit,
      slots
    } = _ref;
    const {
      backgroundColorClasses,
      backgroundColorStyles
    } = useBackgroundColor(toRef(props, "color"));
    const {
      roundedClasses
    } = useRounded(props);
    const vm = getCurrentInstance("VImg");
    const currentSrc = shallowRef("");
    const image = ref();
    const state = shallowRef(props.eager ? "loading" : "idle");
    const naturalWidth = shallowRef();
    const naturalHeight = shallowRef();
    const normalisedSrc = computed(() => {
      return props.src && typeof props.src === "object" ? {
        src: props.src.src,
        srcset: props.srcset || props.src.srcset,
        lazySrc: props.lazySrc || props.src.lazySrc,
        aspect: Number(props.aspectRatio || props.src.aspect || 0)
      } : {
        src: props.src,
        srcset: props.srcset,
        lazySrc: props.lazySrc,
        aspect: Number(props.aspectRatio || 0)
      };
    });
    const aspectRatio = computed(() => {
      return normalisedSrc.value.aspect || naturalWidth.value / naturalHeight.value || 0;
    });
    watch(() => props.src, () => {
      init(state.value !== "idle");
    });
    watch(aspectRatio, (val, oldVal) => {
      if (!val && oldVal && image.value) {
        pollForSize(image.value);
      }
    });
    onBeforeMount(() => init());
    function init(isIntersecting) {
      if (props.eager && isIntersecting)
        return;
      if (SUPPORTS_INTERSECTION && !isIntersecting && !props.eager)
        return;
      state.value = "loading";
      if (normalisedSrc.value.lazySrc) {
        const lazyImg = new Image();
        lazyImg.src = normalisedSrc.value.lazySrc;
        pollForSize(lazyImg, null);
      }
      if (!normalisedSrc.value.src)
        return;
      nextTick(() => {
        var _a;
        emit("loadstart", ((_a = image.value) == null ? void 0 : _a.currentSrc) || normalisedSrc.value.src);
        setTimeout(() => {
          var _a2;
          if (vm.isUnmounted)
            return;
          if ((_a2 = image.value) == null ? void 0 : _a2.complete) {
            if (!image.value.naturalWidth) {
              onError();
            }
            if (state.value === "error")
              return;
            if (!aspectRatio.value)
              pollForSize(image.value, null);
            if (state.value === "loading")
              onLoad();
          } else {
            if (!aspectRatio.value)
              pollForSize(image.value);
            getSrc();
          }
        });
      });
    }
    function onLoad() {
      var _a;
      if (vm.isUnmounted)
        return;
      getSrc();
      pollForSize(image.value);
      state.value = "loaded";
      emit("load", ((_a = image.value) == null ? void 0 : _a.currentSrc) || normalisedSrc.value.src);
    }
    function onError() {
      var _a;
      if (vm.isUnmounted)
        return;
      state.value = "error";
      emit("error", ((_a = image.value) == null ? void 0 : _a.currentSrc) || normalisedSrc.value.src);
    }
    function getSrc() {
      const img = image.value;
      if (img)
        currentSrc.value = img.currentSrc || img.src;
    }
    let timer = -1;
    onBeforeUnmount(() => {
      clearTimeout(timer);
    });
    function pollForSize(img) {
      let timeout = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : 100;
      const poll = () => {
        clearTimeout(timer);
        if (vm.isUnmounted)
          return;
        const {
          naturalHeight: imgHeight,
          naturalWidth: imgWidth
        } = img;
        if (imgHeight || imgWidth) {
          naturalWidth.value = imgWidth;
          naturalHeight.value = imgHeight;
        } else if (!img.complete && state.value === "loading" && timeout != null) {
          timer = window.setTimeout(poll, timeout);
        } else if (img.currentSrc.endsWith(".svg") || img.currentSrc.startsWith("data:image/svg+xml")) {
          naturalWidth.value = 1;
          naturalHeight.value = 1;
        }
      };
      poll();
    }
    const containClasses = computed(() => ({
      "v-img__img--cover": props.cover,
      "v-img__img--contain": !props.cover
    }));
    const __image = () => {
      var _a;
      if (!normalisedSrc.value.src || state.value === "idle")
        return null;
      const img = createVNode("img", {
        "class": ["v-img__img", containClasses.value],
        "style": {
          objectPosition: props.position
        },
        "src": normalisedSrc.value.src,
        "srcset": normalisedSrc.value.srcset,
        "alt": props.alt,
        "crossorigin": props.crossorigin,
        "referrerpolicy": props.referrerpolicy,
        "draggable": props.draggable,
        "sizes": props.sizes,
        "ref": image,
        "onLoad": onLoad,
        "onError": onError
      }, null);
      const sources = (_a = slots.sources) == null ? void 0 : _a.call(slots);
      return createVNode(MaybeTransition, {
        "transition": props.transition,
        "appear": true
      }, {
        default: () => [withDirectives(sources ? createVNode("picture", {
          "class": "v-img__picture"
        }, [sources, img]) : img, [[vShow, state.value === "loaded"]])]
      });
    };
    const __preloadImage = () => createVNode(MaybeTransition, {
      "transition": props.transition
    }, {
      default: () => [normalisedSrc.value.lazySrc && state.value !== "loaded" && createVNode("img", {
        "class": ["v-img__img", "v-img__img--preload", containClasses.value],
        "style": {
          objectPosition: props.position
        },
        "src": normalisedSrc.value.lazySrc,
        "alt": props.alt,
        "crossorigin": props.crossorigin,
        "referrerpolicy": props.referrerpolicy,
        "draggable": props.draggable
      }, null)]
    });
    const __placeholder = () => {
      if (!slots.placeholder)
        return null;
      return createVNode(MaybeTransition, {
        "transition": props.transition,
        "appear": true
      }, {
        default: () => [(state.value === "loading" || state.value === "error" && !slots.error) && createVNode("div", {
          "class": "v-img__placeholder"
        }, [slots.placeholder()])]
      });
    };
    const __error = () => {
      if (!slots.error)
        return null;
      return createVNode(MaybeTransition, {
        "transition": props.transition,
        "appear": true
      }, {
        default: () => [state.value === "error" && createVNode("div", {
          "class": "v-img__error"
        }, [slots.error()])]
      });
    };
    const __gradient = () => {
      if (!props.gradient)
        return null;
      return createVNode("div", {
        "class": "v-img__gradient",
        "style": {
          backgroundImage: `linear-gradient(${props.gradient})`
        }
      }, null);
    };
    const isBooted = shallowRef(false);
    {
      const stop = watch(aspectRatio, (val) => {
        if (val) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              isBooted.value = true;
            });
          });
          stop();
        }
      });
    }
    useRender(() => {
      const responsiveProps = VResponsive.filterProps(props);
      return withDirectives(createVNode(VResponsive, mergeProps({
        "class": ["v-img", {
          "v-img--booting": !isBooted.value
        }, backgroundColorClasses.value, roundedClasses.value, props.class],
        "style": [{
          width: convertToUnit(props.width === "auto" ? naturalWidth.value : props.width)
        }, backgroundColorStyles.value, props.style]
      }, responsiveProps, {
        "aspectRatio": aspectRatio.value,
        "aria-label": props.alt,
        "role": props.alt ? "img" : void 0
      }), {
        additional: () => createVNode(Fragment, null, [createVNode(__image, null, null), createVNode(__preloadImage, null, null), createVNode(__gradient, null, null), createVNode(__placeholder, null, null), createVNode(__error, null, null)]),
        default: slots.default
      }), [[resolveDirective("intersect"), {
        handler: init,
        options: props.options
      }, null, {
        once: true
      }]]);
    });
    return {
      currentSrc,
      image,
      state,
      naturalWidth,
      naturalHeight
    };
  }
});
const allowedDensities = [null, "default", "comfortable", "compact"];
const makeDensityProps = propsFactory({
  density: {
    type: String,
    default: "default",
    validator: (v) => allowedDensities.includes(v)
  }
}, "density");
function useDensity(props) {
  let name = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : getCurrentInstanceName();
  const densityClasses = computed(() => {
    return `${name}--density-${props.density}`;
  });
  return {
    densityClasses
  };
}
const allowedVariants = ["elevated", "flat", "tonal", "outlined", "text", "plain"];
function genOverlays(isClickable, name) {
  return createVNode(Fragment, null, [isClickable && createVNode("span", {
    "key": "overlay",
    "class": `${name}__overlay`
  }, null), createVNode("span", {
    "key": "underlay",
    "class": `${name}__underlay`
  }, null)]);
}
const makeVariantProps = propsFactory({
  color: String,
  variant: {
    type: String,
    default: "elevated",
    validator: (v) => allowedVariants.includes(v)
  }
}, "variant");
function useVariant(props) {
  let name = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : getCurrentInstanceName();
  const variantClasses = computed(() => {
    const {
      variant
    } = unref(props);
    return `${name}--variant-${variant}`;
  });
  const {
    colorClasses,
    colorStyles
  } = useColor(computed(() => {
    const {
      variant,
      color
    } = unref(props);
    return {
      [["elevated", "flat"].includes(variant) ? "background" : "text"]: color
    };
  }));
  return {
    colorClasses,
    colorStyles,
    variantClasses
  };
}
const makeVAvatarProps = propsFactory({
  start: Boolean,
  end: Boolean,
  icon: IconValue,
  image: String,
  text: String,
  ...makeComponentProps(),
  ...makeDensityProps(),
  ...makeRoundedProps(),
  ...makeSizeProps(),
  ...makeTagProps(),
  ...makeThemeProps(),
  ...makeVariantProps({
    variant: "flat"
  })
}, "VAvatar");
const VAvatar = genericComponent()({
  name: "VAvatar",
  props: makeVAvatarProps(),
  setup(props, _ref) {
    let {
      slots
    } = _ref;
    const {
      themeClasses
    } = provideTheme(props);
    const {
      colorClasses,
      colorStyles,
      variantClasses
    } = useVariant(props);
    const {
      densityClasses
    } = useDensity(props);
    const {
      roundedClasses
    } = useRounded(props);
    const {
      sizeClasses,
      sizeStyles
    } = useSize(props);
    useRender(() => createVNode(props.tag, {
      "class": ["v-avatar", {
        "v-avatar--start": props.start,
        "v-avatar--end": props.end
      }, themeClasses.value, colorClasses.value, densityClasses.value, roundedClasses.value, sizeClasses.value, variantClasses.value, props.class],
      "style": [colorStyles.value, sizeStyles.value, props.style]
    }, {
      default: () => [!slots.default ? props.image ? createVNode(VImg, {
        "key": "image",
        "src": props.image,
        "alt": "",
        "cover": true
      }, null) : props.icon ? createVNode(VIcon, {
        "key": "icon",
        "icon": props.icon
      }, null) : props.text : createVNode(VDefaultsProvider, {
        "key": "content-defaults",
        "defaults": {
          VImg: {
            cover: true,
            image: props.image
          },
          VIcon: {
            icon: props.icon
          }
        }
      }, {
        default: () => [slots.default()]
      }), genOverlays(false, "v-avatar")]
    }));
    return {};
  }
});
const makePreview = (contents) => {
  if (!contents) {
    return "";
  }
  const fromMarkdown = fromMarkdownLike(contents);
  const tempDivElement = document.createElement("div");
  tempDivElement.innerHTML = fromMarkdown;
  return tempDivElement.textContent || tempDivElement.innerText || "";
};
const _sfc_main = defineComponent$1({
  name: "MalarkeyConversationListItem",
  components: {
    VAvatar,
    MalarkeyContactAvatar
  },
  props: {
    ...makeVListItemProps(),
    /**
     * The [Twilio Conversation](https://sdk.twilio.com/js/conversations/releases/2.4.0/docs/classes/Conversation.html) object to represent.
     */
    conversation: {
      type: Object,
      default: void 0
    },
    /**
     * Color of the indicator for when all messages have been read by all participants of the conversation
     */
    allReadColor: {
      type: String,
      default: "info"
    },
    /**
     * Color used to indicate that the conversation has unread messages
     */
    unreadColor: {
      type: String,
      default: "error"
    }
  },
  emits: [
    /**
     * Emitted when the user clicks on the conversation list item.
     * @value sid {string} The SID of the conversation.
     */
    "sid"
  ],
  setup(props, { emit }) {
    const mounted2 = ref(false);
    onMounted(() => {
      mounted2.value = true;
    });
    onBeforeUnmount(() => {
      mounted2.value = false;
    });
    const mConversationListItem = ref(null);
    const mConversationListItemMenuAnchor = ref(
      null
    );
    const identity = inject("identity");
    const cron = inject("cron");
    const bus = inject("bus");
    const now = ref(void 0);
    const twilio = inject("twilio");
    const authenticated = computed(() => {
      if (!mounted2.value || !identity || !identity.booted.value) {
        return null;
      }
      if (!identity.authenticated.value) {
        return false;
      }
      return identity.authenticated.value;
    });
    const withVListItemDefaults = useDefaults(props, "VListItem");
    const withMalarkeyConversationListItemDefaults = useDefaults(
      withVListItemDefaults,
      "MalarkeyConversationListItem"
    );
    const unreadMessageCount = ref(0);
    const hasUnreadMessages = computed(() => unreadMessageCount.value > 0);
    const vListItemBindings = computed(() => ({
      ...withMalarkeyConversationListItemDefaults,
      conversation: void 0,
      allReadColor: void 0,
      unreadColor: void 0,
      twoLine: true,
      class: [
        "m-conversation-list-item",
        hasUnreadMessages.value ? "with-unread" : "without-unread"
      ]
    }));
    const conversationItemRaw = computed(() => props.conversation);
    const conversationItem = shallowRef(
      props.conversation
    );
    watch(
      () => conversationItemRaw.value,
      (c) => {
        conversationItem.value = c;
      },
      { immediate: true }
    );
    const sid = computed(
      () => conversationItem.value ? conversationItem.value.sid : void 0
    );
    const participants = ref([]);
    const lastMessage = ref(void 0);
    const lastMessageContents = ref(void 0);
    const lastMessageSender = ref(void 0);
    const notificationLevel = ref(
      void 0
    );
    const lastUpdateAt = ref(void 0);
    const isMuted = computed(() => notificationLevel.value === "muted");
    const isGroupChat = computed(() => participants.value.length > 2);
    const nonUserParticipants = computed(
      () => (
        // @ts-ignore
        [...participants.value].filter(
          (p) => authenticated.value && p.identity !== identity.user.value.id
        )
      )
    );
    const participantLastReadMessageIndecies = ref({});
    const avatarComponent = computed(
      () => isGroupChat.value ? "VAvatar" : "MalarkeyContactAvatar"
    );
    const avatarComponentParticipant = computed(
      () => isGroupChat.value ? null : nonUserParticipants.value[0]
    );
    const avatarComponentBindings = computed(() => {
      const basics = {};
      if (isGroupChat.value) {
        return {
          ...basics,
          icon: "mdi-account-group",
          size: 40
        };
      } else {
        return {
          ...basics,
          identity: "undefined" !== typeof avatarComponentParticipant.value && null !== avatarComponentParticipant.value ? avatarComponentParticipant.value.identity : void 0
        };
      }
    });
    const title = computed(() => {
      if (isGroupChat.value) {
        return toSubjectListString(
          nonUserParticipants.value.map((p) => getDisplayNameOfParticipant(p)).filter((d) => "string" === typeof d)
        );
      } else {
        return getDisplayNameOfParticipant(
          nonUserParticipants.value[0]
        );
      }
    });
    const lastMessageIndex = computed(
      () => lastMessage.value ? "number" === typeof lastMessage.value.index && lastMessage.value.index >= 0 ? lastMessage.value.index : 0 : -1
    );
    const isAllRead = computed(() => {
      if (lastMessageIndex.value < 0 || participants.value.length < 1 || Object.keys(participantLastReadMessageIndecies.value).length < participants.value.length) {
        return false;
      }
      return participants.value.every((p) => {
        const lri = participantLastReadMessageIndecies.value[p.identity];
        return "number" === typeof lri && lri >= lastMessageIndex.value;
      });
    });
    const lastMessagePreview = computed(() => {
      let ret = "";
      if (isGroupChat.value && lastMessageSender.value && lastMessageContents.value && twilio) {
        const rosterItem = twilio.roster.value.find(
          (r) => "undefined" !== typeof r && r.identifier === lastMessageSender.value
        );
        if (rosterItem) {
          const name = stringOr(
            rosterItem.name || void 0,
            stringOr(
              rosterItem.friendlyName || void 0,
              stringOr(rosterItem.identifier || void 0)
            )
          );
          if (name) {
            ret = `${name}: `;
          }
        }
      }
      if (lastMessageContents.value) {
        ret += makePreview(lastMessageContents.value);
      }
      return ret;
    });
    const onClick = (e) => {
      e.preventDefault();
      if (conversationItem.value) {
        emit("sid", conversationItem.value.sid);
      }
    };
    const onCronTick = () => {
      now.value = DateTime.now();
    };
    const showContextMenu = ref(false);
    const contextMenuX = ref(0);
    const contextMenuY = ref(0);
    const contextMenuAnchorBinding = computed(() => ({
      style: {
        top: `${contextMenuY.value}px`,
        left: `${contextMenuX.value}px`
      }
    }));
    const onContextMenu = (e) => {
      e.preventDefault();
      if (showContextMenu.value === true) {
        showContextMenu.value = false;
      }
      debug("requested context menu");
      nextTick(() => {
        if (!mConversationListItem.value || !mConversationListItemMenuAnchor.value) {
          return;
        }
        const rect = mConversationListItem.value.$el.getBoundingClientRect();
        contextMenuX.value = e.clientX - rect.left;
        contextMenuY.value = e.clientY - rect.top;
        showContextMenu.value = true;
      });
      return false;
    };
    const contextMenuBindings = computed(() => ({
      activator: mConversationListItemMenuAnchor.value,
      closeOnBack: false,
      closeOnContentClick: true,
      noClickAnimation: true,
      openOnClick: false,
      openOnFocus: false
    }));
    const setAllMessagesRead = () => {
      if (conversationItem.value) {
        conversationItem.value.setAllMessagesRead().catch(() => {
        });
      }
    };
    const setAllMessagesUnread = () => {
      if (conversationItem.value) {
        conversationItem.value.setAllMessagesUnread().then((unreadCount) => {
          unreadMessageCount.value = unreadCount;
        }).catch(() => {
        });
      }
    };
    const setConversationMuted = () => {
      if (conversationItem.value) {
        conversationItem.value.setUserNotificationLevel("muted").catch(() => {
        });
      }
    };
    const setConversationUnmuted = () => {
      if (conversationItem.value) {
        conversationItem.value.setUserNotificationLevel("default").catch(() => {
        });
      }
    };
    const lastMessageEventAt = shallowRef(void 0);
    const lastMessageAt = computed(() => {
      if (lastMessage.value && lastMessage.value.dateCreated && lastMessageEventAt.value) {
        const v1 = DateTime.fromJSDate(lastMessage.value.dateCreated);
        const v2 = lastMessageEventAt.value;
        return v1 > v2 ? v1 : v2;
      } else if (lastMessage.value && lastMessage.value.dateCreated) {
        return DateTime.fromJSDate(lastMessage.value.dateCreated);
      } else if (lastMessageEventAt.value) {
        return lastMessageEventAt.value;
      }
      return void 0;
    });
    const lastUpdated = computed(() => {
      if (!lastMessageAt.value) {
        return void 0;
      }
      const difference = lastMessageAt.value.diffNow([
        "weeks",
        "days",
        "hours",
        "minutes",
        "seconds"
      ]);
      if (difference.weeks < -1) {
        return lastMessageAt.value.toLocaleString();
      } else if (difference.days < -1) {
        return lastMessageAt.value.toLocaleString({ weekday: "long" });
      } else if (difference.hours < -1) {
        return lastMessageAt.value.toLocaleString(DateTime.TIME_SIMPLE);
      }
      return lastMessageAt.value.toLocaleString(DateTime.TIME_SIMPLE);
    });
    const handleConversationUpdate = (i) => {
      debug("conversation item changed", i);
      if (i) {
        lastMessage.value = i.lastMessage;
        notificationLevel.value = i.notificationLevel;
        if (i.lastMessage && i.lastMessage.dateCreated) {
          lastUpdateAt.value = DateTime.fromJSDate(i.lastMessage.dateCreated);
        } else if (i.dateUpdated) {
          lastUpdateAt.value = DateTime.fromJSDate(i.dateUpdated);
        } else if (i.dateCreated) {
          lastUpdateAt.value = DateTime.fromJSDate(i.dateCreated);
        } else {
          lastUpdateAt.value = void 0;
        }
        i.getParticipants().then((list) => {
          if (conversationItem.value && i.sid === conversationItem.value.sid) {
            participants.value = list;
            participantLastReadMessageIndecies.value = {};
            list.forEach((p) => {
              const lri = p.lastReadMessageIndex;
              if ("number" === typeof lri) {
                participantLastReadMessageIndecies.value[p.identity] = lri;
              } else {
                participantLastReadMessageIndecies.value[p.identity] = 0;
              }
            });
          }
        }).catch(() => {
          if (conversationItem.value && i.sid === conversationItem.value.sid) {
            participants.value = [];
            participantLastReadMessageIndecies.value = {};
          }
        });
        i.getUnreadMessagesCount().then((count) => {
          if (conversationItem.value && i.sid === conversationItem.value.sid) {
            if ("number" === typeof count) {
              unreadMessageCount.value = count;
            } else {
              unreadMessageCount.value = 0;
            }
          }
        }).catch(() => {
          if (conversationItem.value && i.sid === conversationItem.value.sid) {
            unreadMessageCount.value = 0;
          }
        });
        i.getMessages(1).then((paginator) => {
          const message = paginator.items[0];
          if (message) {
            if ("string" === typeof message.body) {
              lastMessageContents.value = message.body;
            } else {
              lastMessageContents.value = void 0;
            }
            if (message.participantSid) {
              lastMessageSender.value = message.participantSid;
            } else {
              lastMessageSender.value = void 0;
            }
          } else {
            lastMessageContents.value = void 0;
            lastMessageSender.value = void 0;
          }
        }).catch(() => {
          lastMessageContents.value = void 0;
          lastMessageSender.value = void 0;
        });
      } else {
        lastMessage.value = void 0;
        notificationLevel.value = void 0;
        participants.value = [];
        unreadMessageCount.value = 0;
        lastUpdateAt.value = void 0;
        participantLastReadMessageIndecies.value = {};
        lastMessageContents.value = void 0;
        lastMessageSender.value = void 0;
      }
    };
    const loadConversationItem = () => {
      debug("Loading Conversation Item", sid.value);
      if (!twilio || !twilio.conversations.value) {
        conversationItem.value = void 0;
        triggerRef(conversationItem);
        nextTick(() => {
          handleConversationUpdate(conversationItem.value);
        });
        return;
      }
      conversationItem.value = twilio.conversations.value.find(
        (c) => c.sid === sid.value
      );
      triggerRef(conversationItem);
      nextTick(() => {
        handleConversationUpdate(conversationItem.value);
      });
      return;
    };
    const onTwilioConversationUpdated = ({
      conversation
    }) => {
      if (conversation.sid === sid.value) {
        loadConversationItem();
      }
    };
    const onTwilioConversationChanged = (conversation) => {
      if (conversation.sid === sid.value) {
        loadConversationItem();
      }
    };
    const onTwilioMessageAddedOrRemoved = (message) => {
      if (message.conversation.sid === sid.value) {
        handleConversationUpdate(message.conversation);
      }
    };
    const onTwilioMessageAdded = (message) => {
      if (message.conversation.sid === sid.value) {
        lastMessageEventAt.value = DateTime.now();
        triggerRef(lastMessageEventAt);
      }
    };
    const onTwilioMessageEdited = ({ message }) => {
      if (message.conversation.sid === sid.value) {
        handleConversationUpdate(message.conversation);
      }
    };
    const onTwilioParticipantJoined = (participant) => {
      if (participant.conversation.sid === sid.value) {
        handleConversationUpdate(participant.conversation);
      }
    };
    const onTwilioParticipantLeft = (participant) => {
      if (participant.conversation.sid === sid.value) {
        handleConversationUpdate(participant.conversation);
      }
    };
    const onTwilioParticipantUpdated = ({
      participant
    }) => {
      if (participant.conversation.sid === sid.value) {
        handleConversationUpdate(participant.conversation);
      }
    };
    watch(() => conversationItem.value, handleConversationUpdate, {
      immediate: true
    });
    watch(
      () => showContextMenu.value,
      (is) => {
        if (!is) {
          contextMenuX.value = 0;
          contextMenuY.value = 0;
        }
      }
    );
    onMounted(() => {
      if (cron) {
        onCronTick();
        cron.$on("* * * * * *", onCronTick);
      }
      if (bus) {
        bus.on(
          "twilio:client:conversationUpdated",
          onTwilioConversationUpdated,
          {
            local: true
          }
        );
        bus.on("twilio:client:conversationAdded", onTwilioConversationChanged, {
          local: true
        });
        bus.on(
          "twilio:client:conversationJoined",
          onTwilioConversationChanged,
          {
            local: true
          }
        );
        bus.on("twilio:client:conversationLeft", onTwilioConversationChanged, {
          local: true
        });
        bus.on("twilio:client:messageAdded", onTwilioMessageAddedOrRemoved, {
          local: true
        });
        bus.on("twilio:client:messageAdded", onTwilioMessageAdded, {
          local: true
        });
        bus.on("twilio:client:messageRemoved", onTwilioMessageAddedOrRemoved, {
          local: true
        });
        bus.on("twilio:client:messageUpdated", onTwilioMessageEdited, {
          local: true
        });
        bus.on("twilio:client:participantJoined", onTwilioParticipantJoined, {
          local: true
        });
        bus.on("twilio:client:participantLeft", onTwilioParticipantLeft, {
          local: true
        });
        bus.on("twilio:client:participantUpdated", onTwilioParticipantUpdated, {
          local: true
        });
      }
    });
    onBeforeUnmount(() => {
      if (cron) {
        cron.$off("* * * * * *", onCronTick);
      }
      if (bus) {
        bus.off(
          // @ts-ignore - stupid typescript
          "twilio:client:conversationUpdated",
          onTwilioConversationUpdated,
          {
            local: true
          }
        );
        bus.off(
          "twilio:client:conversationAdded",
          onTwilioConversationChanged,
          {
            local: true
          }
        );
        bus.off(
          // @ts-ignore - stupid typescript
          "twilio:client:conversationJoined",
          onTwilioConversationChanged,
          {
            local: true
          }
        );
        bus.off("twilio:client:conversationLeft", onTwilioConversationChanged, {
          local: true
        });
        bus.off("twilio:client:messageAdded", onTwilioMessageAddedOrRemoved, {
          local: true
        });
        bus.off("twilio:client:messageAdded", onTwilioMessageAdded, {
          local: true
        });
        bus.off("twilio:client:messageRemoved", onTwilioMessageAddedOrRemoved, {
          local: true
        });
        bus.off("twilio:client:messageUpdated", onTwilioMessageEdited, {
          local: true
        });
        bus.off("twilio:client:participantJoined", onTwilioParticipantJoined, {
          local: true
        });
        bus.off("twilio:client:participantLeft", onTwilioParticipantLeft, {
          local: true
        });
        bus.off(
          "twilio:client:participantUpdated",
          onTwilioParticipantUpdated,
          {
            local: true
          }
        );
      }
    });
    return {
      vListItemBindings,
      onClick,
      avatarComponent,
      avatarComponentParticipant,
      title,
      isMuted,
      unreadMessageCount,
      lastMessage,
      notificationLevel,
      lastUpdated,
      hasUnreadMessages,
      lastMessageIndex,
      isGroupChat,
      isAllRead,
      lastMessagePreview,
      onContextMenu,
      showContextMenu,
      contextMenuBindings,
      contextMenuAnchorBinding,
      mConversationListItem,
      mConversationListItemMenuAnchor,
      setAllMessagesRead,
      setAllMessagesUnread,
      setConversationMuted,
      setConversationUnmuted,
      // for debugging
      participants,
      nonUserParticipants,
      avatarComponentBindings,
      participantLastReadMessageIndecies,
      lastMessageAt
    };
  }
});
export {
  VAvatar as V,
  _sfc_main as _
};
//# sourceMappingURL=MalarkeyConversationListItem.vue_vue_type_script_lang-3da40755.mjs.map
